import {isPlatformBrowser} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {ScriptInjectionStatusService} from '@app/config/script-injection-status.service';
import {DcpCcPsp} from '@app/model/dcp-cc-psp';
import {ConsignmentCardConfig} from '@app/model/dcp-config-service/card-config';
import {
  DelegoRapidPayAddress,
  DelegoRapidPaySDKObject,
  DelegoRapidPayTokenizePaymentResponse,
} from '@app/model/delego-rapidpay/delego-rapidpay';
import {DelegoRapidPayThreeDSecureInfoWithDelegoToken} from '@app/model/delego-rapidpay/delego-rapidpay-authenticate';
import {DelegoRapidPayIFrame, DelegoRapidPayIFrameOptions} from '@app/model/delego-rapidpay/delego-rapidpay-iframe';
import {
  PaymentPayloadType,
  PaymentTransactionData,
  PlaceOrderRequestBody,
} from '@app/model/order/dcp-place-order-body.model';
import {CardPaymentSessionStorageService} from '@app/payment/services/card-payment-session-storage.service';
import {PaymentProvider} from '@app/payment/services/dcp-cc-psp.service';
import {DelegoPaymentErrorService} from '@app/payment/services/delego-rapidpay/error-handling/delego-payment-error.service';
import * as delegoActions from '@app/payment/services/store/actions/delego.action';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {ConfigService, DcpGlobalMessageService, HttpRequestOptions} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {Cart} from '@spartacus/cart/base/root';
import {Address, PaymentDetails, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import jwt_decode from 'jwt-decode';
import * as _ from 'lodash-es';
import {BehaviorSubject, combineLatest, forkJoin, Observable} from 'rxjs';
import {concatMap, filter, first, map, take} from 'rxjs/operators';
import {DcpCheckoutPaymentService} from '../dcp-checkout-payment.service';
import {PaymentProviderService} from '../payment-provider.service';
import {AuthorizationResult, DelegoPaymentState} from '../store/payment-state';
import * as fromDelego from '../store/reducers/delego.reducer';

declare const DelegoRapidPay: DelegoRapidPaySDKObject;
export const alreadyInitializedErrorMessage: string = 'Delego iFrame already initialized';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DelegoRapidpayService extends PaymentProviderService {
  /**
   * MerchantId (sorg) which is going to be sent in openDelegoRapidPayI!Frame call
   */
  private pspMerchantId$ = new BehaviorSubject<string>(null);
  private delegoIFrame: DelegoRapidPayIFrame;
  private isDelegoIframeOpened = new BehaviorSubject<boolean>(false);
  private isDelegoLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store,
    private cardPaymentSessionStorageService: CardPaymentSessionStorageService,
    private kymaService: DcpKymaService,
    private http: HttpClient,
    private configService: ConfigService,
    private scriptInjectionStatusService: ScriptInjectionStatusService,
    private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
    private userFacade: UserAccountFacade,
    private activeCartService: DcpActiveCartService,
    private delegoPaymentErrorService: DelegoPaymentErrorService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    @Inject(PLATFORM_ID) public platformId: string
  ) {
    super();
  }

  /**
   * Ensure external rapidpay has been successfully loaded
   */
  isDelegoRapidPayReady(): boolean {
    try {
      return !!DelegoRapidPay;
    } catch (e) {
      return false;
    }
  }

  openRapidPay(rapidPayOpenOptions: DelegoRapidPayIFrameOptions): DelegoRapidPayIFrame {
    try {
      return DelegoRapidPay.open(rapidPayOpenOptions);
    } catch (e) {
      this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
    }
  }

  closeAddNewCreditCardComponent(): void {
    this.isDelegoIframeOpened.next(false);
  }

  destroyIFrame(): void {
    if (this.delegoIFrame) {
      try {
        this.delegoIFrame.close();
        this.setErrorMessageStatus(false);
      } catch (e) {
        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
        if (isPlatformBrowser(this.platformId)) {
          window.location.reload();
        }
      } finally {
        this.isDelegoIframeOpened.next(false);
        this.delegoIFrame = undefined;
      }
    }
  }

  /**
   * Authorization logic is handled by DelegoSilentPaymentComponent. AuthorizeCart action starts the process
   */
  startCcFlow(): void {
    this.activeCartService
      .getActiveCart()
      .pipe(first(), untilDestroyed(this))
      .subscribe((cart) => {
        const cardType = cart.paymentInfo?.cardType?.code;
        this.store.dispatch(new delegoActions.AuthorizeCart({cardType}));
      });
  }

  isPaymentInfoSet(cart: Cart): boolean {
    return !!cart.paymentInfo?.subscriptionId;
  }

  isAuthenticationSuccessful(authResponse: DelegoRapidPayThreeDSecureInfoWithDelegoToken, cardType: string): boolean {
    return this.delegoPaymentErrorService.isAuthenticationSuccessful(authResponse, cardType);
  }

  decodeJwtResponse<T>(jwt: string): T {
    const jwtDecoded = jwt_decode<{aud: string; body: string; iat: string; jti: string}>(jwt);
    const authorization = JSON.parse(jwtDecoded.body);
    authorization.jwtToken = jwt;
    return authorization;
  }

  convertPaymentDetailsToDelego(cart: Cart): DelegoRapidPayTokenizePaymentResponse {
    const creditCard = cart.paymentInfo;
    return {
      billingAddress: this.convertAddressToDelego(creditCard.billingAddress),
      payment: {
        cardCvv: this.cardPaymentSessionStorageService.getCvv(creditCard.subscriptionId),
        cardExpiration: {year: parseInt(creditCard.expiryYear, 10), month: parseInt(creditCard.expiryMonth, 10)},
        cardIssue: null,
        cardIssueNumber: null,
        cardType: creditCard.cardType.code.toUpperCase(),
        cardholderName: creditCard.accountHolderName,
        fromWallet: false,
        singleUseToken: false,
        threeDSecure: null,
        token: creditCard.subscriptionId,
      },
      shippingAddress: null,
    };
  }

  convertAddressToDelego(addr: Address): DelegoRapidPayAddress {
    return {
      city: addr.town,
      country: addr.country.isocode,
      emailAddress: addr.email,
      firstName: addr.firstName,
      lastName: addr.lastName,
      phoneNumber: undefined,
      postalCode: addr.postalCode,
      region: undefined,
      street: addr.line1,
    };
  }

  convertStateToPlaceOrderPayload(): Observable<PlaceOrderRequestBody> {
    return this.getPaymentState().pipe(
      // ensure successfully CC authorization call
      filter((state) => this.validatePaymentState(state)),
      first(),
      map((state) => this.mapStateToToPlaceOrderPayload(state))
    );
  }

  displayPaymentError(state: DelegoPaymentState): void {
    this.delegoPaymentErrorService
      .getMessageForDelego(state)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((message) => {
        this.dcpGlobalMessageService.addMessage(message, this.constructor.name, 'errorGlobal');
      });
  }

  saveAuthorizationErrors(state: DelegoPaymentState) {
    const authenticationFailed = state.consignmentCodes
      .map((code) => state.data[code].delegoAuthenticationResponse)
      .some((res) => !this.isAuthenticationSuccessful(res, state.cardType));
    const authorizationFailed = state.consignmentCodes
      .map((code) => state.data[code].delegoAuthorizationResponse)
      .some((res) => !res?.authorized);
    if (authenticationFailed || authorizationFailed) {
      const body = this.mapStateToToPlaceOrderPayload(state);
      this.delegoPaymentErrorService.savePaymentError(body);
    }
  }

  /**
   * Gathers necessary data for Delego RapidPay iFrame opening.
   */
  openAddNewCreditCardComponent(currency: string): void {
    if (this.delegoIFrame) {
      // iFrame already loaded
      this.isDelegoIframeOpened.next(true);
      return;
    }
    this.scriptInjectionStatusService
      .getScriptInjectionStatus('delegoRapidPayScript')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!this.isDelegoRapidPayReady()) {
          this.dcpGlobalMessageService.addMessage(
            'delegoRapidpayService is unavailable',
            this?.constructor?.name,
            'errorLogging'
          );
          throw new Error('delegoRapidpayService is unavailable');
        }
        combineLatest([
          this.pspMerchantId$.asObservable().pipe(filter((merchantId) => !!merchantId)),
          this.getCcDelegoJWT(),
        ])
          .pipe(untilDestroyed(this))
          .subscribe(
            ([merchantId, ccPspJWTToken]) => {
              if (!ccPspJWTToken?.token) {
                this.dcpGlobalMessageService.addMessage(
                  'Could not retrieve JWT token for delego rapid pay communication',
                  this?.constructor?.name,
                  'errorLogging'
                );
                throw new Error('Could not retrieve JWT token for delego rapid pay communication');
              }
              if (!currency) {
                this.dcpGlobalMessageService.addMessage(
                  'Fetching cart data has failed!',
                  this?.constructor?.name,
                  'errorLogging'
                );
                throw new Error('Fetching cart data has failed!');
              }
              if (this.delegoIFrame) {
                this.dcpGlobalMessageService.addMessage(
                  alreadyInitializedErrorMessage,
                  this?.constructor?.name,
                  'errorLogging'
                );
                throw new Error(alreadyInitializedErrorMessage);
              }
              const delegoRapidPayIFrameOptions: DelegoRapidPayIFrameOptions = {
                url: this.configService.getString('delegoRapidPay.url'),
                element: '#delegoRapidpayIFrameHost',
                iframeclass: 'delegoRapidpayIFrame',
                token: ccPspJWTToken.token,
                paymentRequest: {
                  currencyCode: currency,
                  // keeping totalValue 0 since price total is not necessary at this step
                  total: 0,
                },
                sorg: merchantId as any,
                events: {
                  onApplicationInitialized: () => {
                    /*
                     * Actions to take once RapidPay is displayed
                     */
                    this.isDelegoIframeOpened.next(true);
                    this.isDelegoLoading$.next(false);
                  },
                  onTokenizeSuccess: (tokenizePaymentResponse) => {
                    /*
                     * Actions to take once RapidPay has tokenized a payment card
                     */
                    this.cardPaymentSessionStorageService.setCvv(
                      tokenizePaymentResponse.payment.token,
                      tokenizePaymentResponse.payment.cardCvv
                    );
                    this.userFacade
                      .get()
                      .pipe(first(), untilDestroyed(this))
                      .subscribe((currentUser: User) => {
                        const preparedPaymentData = this.preparePaymentDetails(tokenizePaymentResponse, currentUser);
                        this.dcpCheckoutPaymentService
                          .createFullCreditCardDetails(preparedPaymentData, 'current')
                          .pipe(untilDestroyed(this))
                          .subscribe(() => {});
                      });
                    this.closeAddNewCreditCardComponent();
                    this.destroyIFrame();
                  },
                  onFailure: (error) => {
                    /*
                     * Actions to take if RapidPay fails to tokenize a payment card (typically a system error)
                     */
                    this.isDelegoLoading$.next(false);
                    this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
                  },
                },
              };
              this.isDelegoLoading$.next(true);
              this.delegoIFrame = this.openRapidPay(delegoRapidPayIFrameOptions);
            },
            (err) => {
              this.dcpGlobalMessageService.addMessage(err, this?.constructor?.name, 'errorLogging');
              throw err;
            }
          );
      });
  }

  /**
   * Saves merchantId to session storage,
   */
  setPspMerchantId(cart: Cart): void {
    const consignmentsMetaData = this.dcpCheckoutPaymentService.extractConsignmentsMetaData(cart);
    const consignmentCardConfigs$: Observable<ConsignmentCardConfig>[] =
      this.dcpCheckoutPaymentService.getConsignmentCardConfigs(consignmentsMetaData);
    forkJoin(consignmentCardConfigs$)
      .pipe(untilDestroyed(this))
      .subscribe((consignmentCardConfigs: ConsignmentCardConfig[]) => {
        consignmentCardConfigs.forEach((consignmentConfig: ConsignmentCardConfig) => {
          this.emitInitialPspMerchantId(consignmentConfig.cardConfig.merchantId);
          this.cardPaymentSessionStorageService.setMerchantId(
            consignmentConfig.consignment.code,
            consignmentConfig.cardConfig.merchantId
          );
          this.cardPaymentSessionStorageService.setThreeDSRequired(
            consignmentConfig.consignment.code,
            !!consignmentConfig.cardConfig['3DSEnable']
          );
        });
      });
  }

  emitInitialPspMerchantId(pspMerchantId: string) {
    if (!this.pspMerchantId$.getValue()) {
      this.pspMerchantId$.next(pspMerchantId);
    }
  }

  isAddNewCreditCardComponentOpened(): Observable<boolean> {
    return this.isDelegoIframeOpened.asObservable();
  }

  isDelegoLoading(): Observable<boolean> {
    return this.isDelegoLoading$.asObservable();
  }

  /**
   * Utilizes dcp-lambda-cc-psp-service-dcp-transaction
   * Retrieves JWT Token, for delego rapidpay communication
   */
  getCcDelegoJWT(): Observable<DcpCcPsp> {
    return this.kymaService
      .prepareAuthConfig()
      .pipe(concatMap((config: HttpRequestOptions) => this.http.request('GET', this.getDelegoTokenUrl(), config)));
  }

  /**
   * Returns true if authorization is finished
   * Throws error when state contains errors
   */
  private validatePaymentState(state: DelegoPaymentState): boolean {
    if (_.isEmpty(state) || state.start) {
      throw state;
    }
    if (state.loading) {
      return false;
    }
    if (state.error) {
      throw state;
    }
    for (const consignmentCode of state.consignmentCodes) {
      const authenticationNotRequired = state.data[consignmentCode].authenticationNotRequired;
      const authentication = state.data[consignmentCode].delegoAuthenticationResponse;
      const authorization = state.data[consignmentCode].delegoAuthorizationResponse;
      if (authenticationNotRequired) {
        if (!authorization.authorized) {
          throw state;
        }
      } else if (!this.isAuthenticationSuccessful(authentication, state.cardType) || !authorization.authorized) {
        throw state;
      }
    }
    return true;
  }

  private mapStateToToPlaceOrderPayload(state: DelegoPaymentState): PlaceOrderRequestBody {
    if (!state?.data) {
      return {};
    }
    const result: PlaceOrderRequestBody = {
      paymentResponse: [],
    };
    for (const consignmentCode of state.consignmentCodes) {
      const consignmentState = state.data[consignmentCode];
      const merchantId = consignmentState.merchantId;
      result.paymentResponse.push(this.buildPaymentResponse(state, consignmentCode, consignmentState, merchantId));
    }
    return result;
  }

  private buildPaymentResponse(
    state: DelegoPaymentState,
    consignmentCode: string,
    consignmentState: AuthorizationResult,
    merchantId: string
  ): PaymentTransactionData {
    if (consignmentState.authenticationNotRequired) {
      const payloadType = consignmentState.delegoAuthorizationResponse.authorized
        ? PaymentPayloadType.PAYMENT_TRANSACTION
        : PaymentPayloadType.PAYMENT_AUTHORIZATION_FAILURE;
      const jwtToken = consignmentState.delegoAuthorizationResponse.jwtToken;
      return new PaymentTransactionData(consignmentCode, jwtToken, merchantId, payloadType);
    } else {
      if (!this.isAuthenticationSuccessful(consignmentState.delegoAuthenticationResponse, state.cardType)) {
        const jwtToken = consignmentState.delegoAuthenticationResponse.jwtToken;
        return new PaymentTransactionData(
          consignmentCode,
          jwtToken,
          merchantId,
          PaymentPayloadType.PAYMENT_AUTHENTICATION_FAILURE
        );
      } else {
        const payloadType = consignmentState.delegoAuthorizationResponse.authorized
          ? PaymentPayloadType.PAYMENT_TRANSACTION
          : PaymentPayloadType.PAYMENT_AUTHORIZATION_FAILURE;
        const jwtToken = consignmentState.delegoAuthorizationResponse.jwtToken;
        return new PaymentTransactionData(consignmentCode, jwtToken, merchantId, payloadType);
      }
    }
  }

  private getPaymentState(): Observable<DelegoPaymentState> {
    return this.store.select(fromDelego.getState).pipe(untilDestroyed(this));
  }

  /**
   * Retrieves cc-psp service token url
   */
  private getDelegoTokenUrl(): string {
    return this.kymaService.getUrl('delegoCcPSP') + this.configService.getString('kyma.delegoCcPSP.token');
  }

  /**
   * Prepares data for https://api.d1.store.net.sap/rest/v2/swagger-ui.html#/Carts/createCartPaymentDetails endpoint
   *
   * @param tokenizeResponse: DelegoRapidPay Payment response data
   * @param user: Utilized for B2BUnit SOLD_TO address
   */
  private preparePaymentDetails(tokenizeResponse: DelegoRapidPayTokenizePaymentResponse, user: User): PaymentDetails {
    const paymentDetails = this.dcpCheckoutPaymentService.prepareBillingAddress(user);
    return {
      ...paymentDetails,
      cardNumber: tokenizeResponse.payment.token,
      cardType: {
        code: tokenizeResponse.payment.cardType.toLowerCase(),
        name: tokenizeResponse.payment.cardType.toLowerCase().replace(/^\w/, (firstChar) => firstChar.toUpperCase()),
      },
      cvn: null,
      defaultPayment: true,
      expiryMonth: `${tokenizeResponse.payment.cardExpiration.month}`,
      expiryYear: `${tokenizeResponse.payment.cardExpiration.year}`,
      saved: true,
      subscriptionId: tokenizeResponse.payment.token,
      paymentProvider: PaymentProvider.DELEGO,
    };
  }
}
